import type { ReactNode } from 'react'
import { fbt, useLocaleContext } from 'fbtee'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { SectionHeader } from '@/components/SectionHeader'
import { entryText, type Digest } from '@/lib/whatsNew'
import { placeholders } from '@/lib/skeletonUtils'
import { useForceLoading } from '@/lib/skeletonDebug'
import type { ChangelogCategory, ChangelogEntry } from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Frozen when the dialog opens — opening advances the watermark the live digest reads. Absent during the close animation. */
  digest?: Digest
  loading?: boolean
  /** Replaces the "since your last visit" subtitle, for a digest that isn't one. */
  description?: ReactNode
}

function categoryLabel(category: ChangelogCategory): string {
  switch (category) {
    case 'planning':
      return fbt('Planning', 'Category of an app feature announcement')
    case 'social':
      return fbt('Social', 'Category of an app feature announcement')
    case 'live-day':
      return fbt('Live day', 'Category of an app feature announcement')
    case 'content':
      return fbt('Lineups & events', 'Category of an app feature announcement')
    case 'app':
      return fbt('App', 'Category of an app feature announcement')
  }
}

function EntryRow({ entry, showCategory }: { entry: ChangelogEntry; showCategory: boolean }) {
  const { locale } = useLocaleContext()
  const { title, body } = entryText(entry, locale)
  return (
    <li className="space-y-1">
      {showCategory && <Badge variant="secondary">{categoryLabel(entry.category)}</Badge>}
      <p className="text-sm font-medium">{title}</p>
      <p className="text-sm text-muted-foreground">{body}</p>
    </li>
  )
}

function DigestBody({ loading: loadingProp, digest }: { loading?: boolean; digest?: Digest }) {
  const loading = useForceLoading(loadingProp)
  if (loading) {
    return (
      <ul className="space-y-4">
        {placeholders(3).map((_, i) => (
          <li key={i} className="space-y-1.5">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </li>
        ))}
      </ul>
    )
  }
  if (!digest) return null

  if (digest.mode === 'all') {
    return (
      <ul className="space-y-4">
        {digest.entries.map(e => (
          <EntryRow key={e.id} entry={e} showCategory />
        ))}
      </ul>
    )
  }

  return (
    <div className="space-y-4">
      {digest.groups.map(g => (
        <section key={g.category}>
          <SectionHeader>{categoryLabel(g.category)}</SectionHeader>
          <ul className="space-y-4">
            {g.entries.map(e => (
              <EntryRow key={e.id} entry={e} showCategory={false} />
            ))}
          </ul>
        </section>
      ))}
      {digest.hiddenCount > 0 && (
        <p className="text-sm text-muted-foreground">
          <fbt desc="Footer of the What's new highlights listing how many smaller updates were left out">
            and{' '}
            <fbt:plural count={digest.hiddenCount} showCount="yes" many="more improvements">
              more improvement
            </fbt:plural>
          </fbt>
        </p>
      )}
    </div>
  )
}

export function WhatsNewDialog({ open, onOpenChange, digest, loading, description }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="whats-new-dialog" className="max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{fbt("What's new", "Title of the What's new dialog listing recent app features")}</DialogTitle>
          <DialogDescription>
            {description ?? (digest?.mode === 'highlights' ? (
              <fbt desc="Subtitle of the What's new dialog when showing only the top updates after a long absence">
                Highlights since your last visit
              </fbt>
            ) : (
              <fbt desc="Subtitle of the What's new dialog listing every update since the user's last visit">
                Since your last visit
              </fbt>
            ))}
          </DialogDescription>
        </DialogHeader>
        <DigestBody loading={loading} digest={digest} />
      </DialogContent>
    </Dialog>
  )
}
